import { MutationFunction } from '@tanstack/react-query';

/**
 * A connection dropped later than this was not a lost packet: the request most
 * likely reached the server and hung there until something on the way cut it
 * off. Such a failure is not retried — repeating it would run the same heavy
 * job again — and is tagged `failure_speed:slow`.
 */
export const SLOW_FAILURE_MS = 5000;

const attemptDurations = new WeakMap<object, number>();

/**
 * Remembers how long the failed attempt took, so the retry policy and the
 * Sentry report can tell a connection that never got through from one that
 * timed out.
 */
export const withAttemptDuration =
  <TData, TVariables>(
    mutationFn: MutationFunction<TData, TVariables>
  ): MutationFunction<TData, TVariables> =>
  async (variables) => {
    const startedAt = Date.now();

    try {
      return await mutationFn(variables);
    } catch (error) {
      if (error && typeof error === 'object') {
        attemptDurations.set(error, Date.now() - startedAt);
      }

      throw error;
    }
  };

/** Duration of the attempt that threw `error`; `undefined` when it was not timed. */
export const getAttemptDuration = (error: unknown): number | undefined =>
  error && typeof error === 'object' ? attemptDurations.get(error) : undefined;

export const isSlowFailure = (error: unknown): boolean =>
  (getAttemptDuration(error) ?? 0) >= SLOW_FAILURE_MS;
